/**
 * <category-switcher> — Lash / Nail vertical toggle.
 *
 * Plain <a> pills, progressively enhanced:
 *  - Persists the chosen key in localStorage (when data-persist="true").
 *  - Marks the active pill, based on the current URL or the stored choice.
 *  - Drives <html data-cs-active="<key>">. The section's generated CSS hides
 *    any [data-vertical] block whose key isn't active, so toggling swaps
 *    content instantly with no reload and no flash on first paint (an inline
 *    script sets the attribute before those blocks are parsed).
 *  - With no [data-vertical] blocks on the page, pills just navigate to their
 *    collection (URL fallback / SEO-safe).
 *
 * Tag any section as switchable by giving it data-vertical="lash" (or "nail").
 * Featured collection sections expose a "Category switcher key" setting for this.
 */
(function () {
  "use strict";

  var STORAGE_KEY = "nina-vertical";

  function readStored() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeStored(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* ignore (private mode / disabled storage) */
    }
  }

  function hasSwappableContent() {
    return !!document.querySelector("[data-vertical]");
  }

  /**
   * A slider (grid-slider carousel / slideshow-component) that initialized while
   * its block was hidden (display:none) measured zero width, so it renders blank
   * when revealed and a plain resize() often won't recover it. The reliable fix
   * is to fully rebuild it once the block is visible.
   *
   * grid-slider is a custom element, so the DOM node exposes destroySlider() /
   * checkSlidesSize() (its own re-init path). We force a reflow first so it
   * measures real dimensions, rebuild, then retry once more on a timer as a
   * belt-and-suspenders against slow layout/image settling.
   */
  function rebuildSlidersIn(block) {
    // Force the now-visible block to lay out before we measure.
    void block.offsetHeight;
    block.querySelectorAll("grid-slider").forEach(function (slider) {
      if (typeof slider.destroySlider === "function" && typeof slider.checkSlidesSize === "function") {
        try { slider.destroySlider(); } catch (e) {}
        void slider.offsetWidth; // reflow
        try { slider.checkSlidesSize(); } catch (e) {}
      } else {
        slider.dispatchEvent(new CustomEvent("theme:slider:resize", { bubbles: true }));
      }
    });
  }

  // Height of a sticky/fixed header sitting at the top, so we can land the
  // scroll at the true top of the target instead of behind the header.
  function getHeaderOffset() {
    var header =
      document.querySelector("[data-header-height]") ||
      document.querySelector("#shopify-section-header") ||
      document.querySelector(".shopify-section-header");
    if (!header) return 0;
    var pos = window.getComputedStyle(header).position;
    if (pos !== "fixed" && pos !== "sticky") return 0;
    return header.getBoundingClientRect().height;
  }

  // Scroll to the TOP of a revealed section. We wait for the reveal + slideshow
  // resize to settle first, otherwise the target grows after we scroll and we
  // end up landing in the middle of it.
  function scrollToTop(el) {
    var isAnchor = el.hasAttribute && el.hasAttribute("data-cs-anchor");
    var extra = parseInt(el.getAttribute && el.getAttribute("data-cs-anchor-offset"), 10);
    if (isNaN(extra)) extra = 0;
    var doScroll = function () {
      var y = el.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset() - 8 - extra;
      if (y < 0) y = 0;
      try {
        window.scrollTo({ top: y, behavior: "smooth" });
      } catch (e) {
        window.scrollTo(0, y);
      }
    };
    // A zero-height anchor is a stable target, so it can scroll almost at once.
    // Other targets can still be resizing (slideshow), so wait for them to settle.
    setTimeout(doScroll, isAnchor ? 120 : 320);
  }

  function refreshSlidersFor(key) {
    var blocks = document.querySelectorAll('[data-vertical="' + key + '"]');
    if (!blocks.length) return;

    // Re-init sliders that measured their width while hidden — but do it in a
    // SCOPED way (call each element's own resize method), never via the global
    // theme:resize:width event, which would reset every marquee on the page
    // (e.g. the always-on header marquee).
    var slideshows = [];
    var tickers = [];
    blocks.forEach(function (b) {
      if (b.matches && b.matches("slideshow-component")) slideshows.push(b);
      b.querySelectorAll("slideshow-component").forEach(function (s) { slideshows.push(s); });
      if (b.matches && b.matches("ticker-bar")) tickers.push(b);
      b.querySelectorAll("ticker-bar").forEach(function (t) { tickers.push(t); });
    });

    function resizeSlideshow(s) {
      try { if (typeof s.getTallestSlideItem === "function") s.getTallestSlideItem(); } catch (e) {}
      try { if (s.flkty && typeof s.flkty.resize === "function") s.flkty.resize(); } catch (e) {}
    }

    // A marquee set up while hidden gets a 0s animation (measured 0 width) and
    // its checkWidth() skips re-setup once cloned. So tear its clones down first,
    // then let checkWidth() rebuild it with the now-correct visible width.
    function resizeTicker(t) {
      try {
        var scale = t.querySelector("[data-ticker-scale]");
        var text = t.querySelector("[data-ticker-text]");
        if (scale) {
          scale.querySelectorAll("[data-clone]").forEach(function (c) {
            if (c.parentNode) c.parentNode.removeChild(c);
          });
        }
        if (text) text.classList.remove("ticker--animated");
        if (typeof t.checkWidth === "function") t.checkWidth();
      } catch (e) {}
    }

    // Background videos are paused by the browser while hidden — play them when
    // their vertical is revealed.
    function playVideos(b) {
      b.querySelectorAll("video").forEach(function (v) {
        try {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } catch (e) {}
      });
    }

    var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
    raf(function () {
      raf(function () {
        blocks.forEach(rebuildSlidersIn); // grid-slider carousels
        blocks.forEach(playVideos);
        slideshows.forEach(resizeSlideshow);
        tickers.forEach(resizeTicker);
        // Retry once more after layout/images settle.
        setTimeout(function () {
          blocks.forEach(rebuildSlidersIn);
          blocks.forEach(playVideos);
          slideshows.forEach(resizeSlideshow);
          tickers.forEach(resizeTicker);
        }, 250);
      });
    });
  }

  if (customElements.get("category-switcher")) return;

  customElements.define(
    "category-switcher",
    class extends HTMLElement {
      connectedCallback() {
        this.persist = this.getAttribute("data-persist") === "true";
        this.pills = Array.prototype.slice.call(
          this.querySelectorAll(".category-switcher__pill")
        );
        if (!this.pills.length) return;

        this.keys = this.pills.map(function (p) {
          return (p.getAttribute("data-vertical-key") || "").toLowerCase();
        });

        this.onClick = this.handleClick.bind(this);
        this.pills.forEach(
          function (pill) {
            pill.addEventListener("click", this.onClick);
          }.bind(this)
        );

        this.setActive(this.resolveInitialKey());
      }

      disconnectedCallback() {
        if (!this.pills) return;
        this.pills.forEach(
          function (pill) {
            pill.removeEventListener("click", this.onClick);
          }.bind(this)
        );
      }

      /** Decide which pill should start active. */
      resolveInitialKey() {
        var path = window.location.pathname.toLowerCase();
        var hash = window.location.hash.replace("#", "").toLowerCase();

        // 1. Explicit hash (#lash) wins.
        if (hash && this.keys.indexOf(hash) !== -1) return hash;

        // 2. On a collection page matching one of the pill links.
        for (var i = 0; i < this.pills.length; i++) {
          var href = this.pills[i].getAttribute("href") || "";
          var slug = href.split("?")[0].replace(/\/+$/, "");
          if (slug && slug !== "#!" && path.indexOf(slug.toLowerCase()) !== -1) {
            return this.keys[i];
          }
        }

        // 3. Stored choice, then 4. first pill.
        var stored = this.persist ? readStored() : null;
        if (stored && this.keys.indexOf(stored) !== -1) return stored;
        return this.keys[0];
      }

      handleClick(event) {
        var pill = event.currentTarget;
        var key = (pill.getAttribute("data-vertical-key") || "").toLowerCase();

        // Instant in-page swap only when the page has swappable content.
        if (hasSwappableContent()) {
          event.preventDefault();
          this.setActive(key);
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, "", "#" + key);
          }
          // Prefer a dedicated invisible Scroll anchor (stable target). Fall
          // back to the first Marquee, then Slideshow, Banner image, then any
          // tagged content.
          var targetEl =
            document.querySelector('[data-cs-anchor][data-vertical="' + key + '"]') ||
            document.querySelector("[data-cs-anchor]:not([data-vertical])") ||
            document.querySelector("[data-cs-anchor]") ||
            document.querySelector('.shopify-section--marquee [data-vertical="' + key + '"]') ||
            document.querySelector('[data-section-type="slider"][data-vertical="' + key + '"]') ||
            document.querySelector('[data-section-type="banner-image"][data-vertical="' + key + '"]') ||
            document.querySelector('[data-vertical="' + key + '"]');
          // Scroll straight to an anchor; otherwise align to the section wrapper.
          var target;
          if (targetEl && targetEl.hasAttribute("data-cs-anchor")) {
            target = targetEl;
          } else {
            target = (targetEl && targetEl.closest(".shopify-section")) || targetEl;
          }
          if (target) scrollToTop(target);
          return;
        }

        // Otherwise remember the choice and let the link navigate.
        if (this.persist) writeStored(key);
      }

      setActive(key) {
        if (!key) return;
        document.documentElement.setAttribute("data-cs-active", key);

        this.pills.forEach(function (pill) {
          var pillKey = (
            pill.getAttribute("data-vertical-key") || ""
          ).toLowerCase();
          var isActive = pillKey === key;
          pill.classList.toggle("is-active", isActive);
          pill.setAttribute("aria-selected", isActive ? "true" : "false");
        });

        if (this.persist) writeStored(key);
        refreshSlidersFor(key);
      }
    }
  );

  // Reveal on scroll: draw the keyword circle + play the headline entrance once.
  function reveal(selector, cls, threshold) {
    var els = document.querySelectorAll(selector);
    if (!els.length) return;
    if (!("IntersectionObserver" in window)) {
      els.forEach(function (el) {
        el.classList.add(cls);
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add(cls);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: threshold }
    );
    els.forEach(function (el) {
      io.observe(el);
    });
  }

  function initHeadlineFx() {
    // Mark mastheads animation-ready only when IO is available (no-JS = visible).
    if ("IntersectionObserver" in window) {
      document.querySelectorAll(".split-hero__masthead").forEach(function (m) {
        m.classList.add("cs-anim");
      });
    }
    reveal(".split-hero__masthead", "is-in", 0.35);
    reveal(".cs-circle", "is-drawn", 0.6);
  }

  if (document.readyState !== "loading") initHeadlineFx();
  else document.addEventListener("DOMContentLoaded", initHeadlineFx);
})();
